import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons"
import { Button, Empty, Flex, Form, Skeleton, Typography } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router"
import { Link, useSearchParams } from "react-router-dom"

import { type DocumentScopeSectionProjectTypes } from "../../../components/Proposal&Contracts/document.types.ts"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import type { RootState } from "../../../store.ts"
import {
  createProposal,
  fetchProposalInitialData,
  generateProposal,
  getProposalPreview,
  saveProposal,
  sendProposalToApproval
} from "../redux/proposals.thunks.ts"
import type { ProposalRequestData } from "../redux/proposals.thunks.types.ts"
import { ProposalAddPreviewHtml } from "./components/ProposalAddPreviewHTML.tsx"
import ProposalForm from "./components/ProposalForm.tsx"
import { type ProposalFormData } from "./components/proposal-form-data.tsx"
import { defaultClientResponsibilities } from "./components/sections/ProposalClientResponsibilitiesSection.tsx"
import { defaultProposalFormCostSummary } from "./components/sections/ProposalCostSummarySection.tsx"
import { defaultExclusions } from "./components/sections/ProposalExclusionsSection.tsx"
import { defaultInclusions } from "./components/sections/ProposalInclusionsSection.tsx"
import { defaultTermsAndConditions } from "./components/sections/ProposalTermsSection.tsx"
import { defaultProposalFormWarrantyItems } from "./components/sections/ProposalWarrantySection.tsx"

function ProposalAddPage({
  saving,
  createProposal,
  createProposalPreview,
  generateProposal,
  getProposalInitialData,
  saveProposal,
  sendProposalToApproval
}: ConnectedProps<typeof connector>) {
  const [form] = Form.useForm<ProposalFormData>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [previewHTML, setPreviewHTML] = useState<string | null>(null)
  const [formData, setFormData] = useState<ProposalRequestData | null>(null)
  const [fetchingInitialData, setFetchingInitialData] = useState(false)
  const [projectTypes, setProjectTypes] =
    useState<DocumentScopeSectionProjectTypes>([])

  const leadId = searchParams.get("leadID")

  const handleInitialForm = useCallback(async () => {
    if (!leadId) return

    try {
      setFetchingInitialData(true)
      const data = await getProposalInitialData(leadId)?.unwrap()

      if (data) {
        const customer = data?.customer || {}
        const company = data?.company || {}
        const projectTypeDetails = data?.project?.project_type_details

        const defaults = {
          inclusions: defaultInclusions,
          exclusions: defaultExclusions,
          client_responsibilities: defaultClientResponsibilities,
          terms_and_conditions: defaultTermsAndConditions,
          cost_summary: defaultProposalFormCostSummary
        }

        form.setFieldsValue({
          ...defaults,
          ...data,
          project_phases: data?.project_phases?.map((e) => ({
            ...e,
            duration_days: e?.duration_days || null
          })),
          customer: { ...customer, state: customer?.state || undefined },
          company: {
            ...company,
            logo: company?.logo_url,
            state: company?.state || undefined
          },
          warranty: {
            ...data?.warranty,
            items: data?.warranty?.items || defaultProposalFormWarrantyItems
          },
          visible_blocks: [
            "customer",
            "project_phases",
            "inclusions",
            "exclusions",
            "client_responsibilities",
            "cost_summary",
            "payment_schedule",
            "warranty",
            "terms_and_conditions"
          ]
        })

        if (projectTypeDetails) {
          setProjectTypes(projectTypeDetails)
        }
      }
    } finally {
      setFetchingInitialData(false)
    }
  }, [leadId, getProposalInitialData, form])

  useEffect(() => {
    void handleInitialForm()
  }, [leadId, handleInitialForm])

  useEffect(() => {
    generateDocumentTitle("Add Proposal")
  }, [])

  if (!leadId) {
    return <Empty description={"Please provide the lead ID."} />
  }

  if (fetchingInitialData) {
    return <Skeleton.Node className={"size-full"} />
  }

  const handleCreateProposal = async (data: Partial<ProposalFormData>) => {
    const params = { ...data, lead_id: leadId }

    setFormData(params)
    const html = await createProposalPreview(params)?.unwrap()
    if (html) setPreviewHTML(html)
  }

  const handleGenerateProposal = async () => {
    if (!formData) return

    const proposalId = await createProposal(formData)?.unwrap()
    if (proposalId)
      generateProposal({ data: formData, navigate, leadId, proposalId })
  }

  const handleSendToApproval = async () => {
    if (!formData) return

    const proposalId = await createProposal(formData)?.unwrap()
    if (proposalId)
      sendProposalToApproval({ data: formData, navigate, leadId, proposalId })
  }

  const handleSaveProposal = () => {
    if (!formData) return
    saveProposal({ data: formData, navigate })
  }

  return (
    <div className={"grid"}>
      {previewHTML && (
        <ProposalAddPreviewHtml
          html={previewHTML}
          saving={saving}
          onClickGenerate={handleGenerateProposal}
          onClickSaveDraft={handleSaveProposal}
          onClickSendToApproval={handleSendToApproval}
          onClose={() => setPreviewHTML(null)}
        />
      )}
      <Flex
        vertical
        gap={"small"}
        className={"max-h-[calc(100vh-90px)] overflow-auto"}
      >
        <Flex
          align={"center"}
          justify={"space-between"}
          className={"sticky top-0 z-10 bg-background"}
        >
          <Typography.Title level={2}>Create New Proposal</Typography.Title>
          <Link to={RoutesPaths.lead.view(leadId)}>
            <Button icon={<ArrowLeftOutlined />}>Back to Lead</Button>
          </Link>
        </Flex>
        <ProposalForm
          innerForm={form}
          saving={saving}
          projectTypes={projectTypes}
          onSubmit={handleCreateProposal}
          checkDirty={!previewHTML}
        />
      </Flex>
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  saving: state.proposals.saveFetching
})

const mapDispatchToProps = {
  createProposal,
  createProposalPreview: getProposalPreview,
  getProposalInitialData: fetchProposalInitialData,
  generateProposal: generateProposal,
  saveProposal: saveProposal,
  sendProposalToApproval: sendProposalToApproval
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProposalAddPage)
